import axios from 'axios';
import sage300AdapterService from '@/Services/sage300AdapterService';
import { Storage } from '@/Services/Storage';

/**
 * Sage 300 Integration Module
 * Purpose: Discover/store Sage 300 API key; cache adapter customers/items with TTL.
 * Feature flag: isSage300Integrated = Boolean(sage300ApiKey)
 *
 * API key is retrieved from SubscriptionManager:
 *   GET /integrations/checkSageRegistration/{storeDB}
 *   Returns { apikey: "sage-xxx..." } if registered, or {} if not.
 */

const SAGE300_TTL_MS = 300000; // 5 minutes

export default {
    namespaced: true,
    state: {
        sage300ApiKey: null,
        sage300Customers: [],
        sage300Items: [],
        customersFetchedAt: null,
        itemsFetchedAt: null,
    },
    getters: {
        isSage300Integrated: (state) => Boolean(state.sage300ApiKey),
        sage300ApiKey: (state) => state.sage300ApiKey,

        sage300CustomersMap: (state) => {
            const map = {};
            state.sage300Customers.forEach(customer => {
                const key = customer.customerCode;
                if (key) map[key] = customer;
            });
            return map;
        },

        sage300ItemsMap: (state) => {
            const map = {};
            state.sage300Items.forEach(item => {
                const key = item.itemNumber || item.ItemNumber || item.itemCode || item.ItemCode;
                if (key) map[key] = item;
            });
            return map;
        },
    },
    mutations: {
        SET_SAGE300_API_KEY(state, apiKey) {
            state.sage300ApiKey = apiKey;
        },
        SET_SAGE300_CUSTOMERS(state, customers) {
            // Only store mapped fields, excluding extra Sage 300 fields like vat, lpo, DiscountType, Symbol, etc.
            // Skip customers without a valid customerCode to avoid duplicate key errors in Vue
            state.sage300Customers = customers
                .map(d => ({
                    customerCode: d.customerCode || d.CustomerCode || d.cardCode || d.CardCode || d.Id || d.id || '',
                    customerName: d.customerName || d.CustomerName || d.name || d.cardName || d.CardName || d.Name || '',
                    contact: d.contact || d.ContactPerson || d.email || d.Email || '',
                    phone: d.phone || d.Phone || d.CellNumber || d.Cell || '',
                    creditLimit: d.creditLimit ?? d.CreditLimit ?? d.Balance ?? 0,
                }))
                .filter(c => c.customerCode); // Skip entries with empty customerCode
            state.customersFetchedAt = Date.now();
        },
        SET_SAGE300_ITEMS(state, items) {
            state.sage300Items = items;
            state.itemsFetchedAt = Date.now();
        },
        CLEAR_SAGE300_CUSTOMERS(state) {
            state.sage300Customers = [];
            state.customersFetchedAt = null;
        },
        CLEAR_SAGE300_ITEMS(state) {
            state.sage300Items = [];
            state.itemsFetchedAt = null;
        },
    },
    actions: {
        /**
         * Fetch Sage 300 API key from SubscriptionManager.
         * Uses the existing checkSageRegistration endpoint.
         */
        async fetchSage300ApiKey({ commit }, storeDB = null) {
            try {
                if (!storeDB) {
                    storeDB = await Storage.getCurrentStoreDB();
                }
                const url = `${window.subscriptionTomcatApi}integrations/checkSageRegistration/${storeDB}`;
                const { data } = await axios.get(url);
                const apiKey = data?.apikey || data?.apiKey || null;
                commit('SET_SAGE300_API_KEY', apiKey);
                return apiKey;
            } catch (error) {
                console.warn('Failed to fetch Sage 300 API key:', error);
                commit('SET_SAGE300_API_KEY', null);
                return null;
            }
        },

        async fetchAdapterCustomers({ commit, state }, params = {}) {
            if (!state.sage300ApiKey) {
                console.warn('Cannot fetch adapter customers: Sage 300 not integrated');
                return [];
            }
            try {
                const customers = await sage300AdapterService.fetchSage300Customers(state.sage300ApiKey, params);
                commit('SET_SAGE300_CUSTOMERS', customers);
                return customers;
            } catch (error) {
                console.error('Error fetching Sage 300 adapter customers:', error);
                return [];
            }
        },

        async fetchAdapterCustomersIfNeeded({ state, dispatch }, params = {}) {
            const now = Date.now();
            const isExpired = !state.customersFetchedAt || (now - state.customersFetchedAt) > SAGE300_TTL_MS;
            if (isExpired) {
                return dispatch('fetchAdapterCustomers', params);
            }
            return state.sage300Customers;
        },

        async fetchAdapterItems({ commit, state }, params = {}) {
            if (!state.sage300ApiKey) {
                console.warn('Cannot fetch adapter items: Sage 300 not integrated');
                return [];
            }
            try {
                const items = await sage300AdapterService.fetchSage300Items(state.sage300ApiKey, params);
                commit('SET_SAGE300_ITEMS', items);
                return items;
            } catch (error) {
                console.error('Error fetching Sage 300 adapter items:', error);
                return [];
            }
        },

        async fetchAdapterItemsIfNeeded({ state, dispatch }, params = {}) {
            const now = Date.now();
            const isExpired = !state.itemsFetchedAt || (now - state.itemsFetchedAt) > SAGE300_TTL_MS;
            if (isExpired) {
                return dispatch('fetchAdapterItems', params);
            }
            return state.sage300Items;
        },

        clearSage300Cache({ commit }) {
            commit('CLEAR_SAGE300_CUSTOMERS');
            commit('CLEAR_SAGE300_ITEMS');
        },
    },
};
